import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import React, { useContext } from "react";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import useCoinCard from "@shared/features/portfolio/coins/useCoinCard.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { DISPLAY_CURRENCIES } from "common/constants.ts";
import { ConsolidatedCoinData, PrecisionType } from "@shared/types";
import TextWithPrecision from "common/Text/TextWithPrecision.tsx";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import LiveSmallChart from "common/LiveComponents/LiveSmallChart.tsx";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon.tsx";
import { getCoinExtraDetailsRoute } from "utils/routes.tsx";
import { useHistory } from "react-router-dom";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import { RootState } from "app/store.ts";
import { PrecisionConfig } from "types";
import useMedia from "utils/hooks/useMedia.ts";
import { NewLookLiveTrendCell } from "features/portfolio/NewLook/NewLookTrendCell.tsx";

interface useAssetItemProps {
  isPositions?: boolean;
  precisionSelector?: (state: RootState) => PrecisionConfig | undefined;
}

const useAssetItem = ({
  isPositions,
  precisionSelector,
}: useAssetItemProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const history = useHistory();
  const { getUpdatedPath } = useHistoryWithQuery();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    getAsset,
    getExchange: getCoinExchange,
    getInitialPrice,
  } = useCoinCard({});
  const { getPrecision } = usePrecisions();
  const isSmallScreen = useMedia([`(max-width: 1600px)`], [true], false);

  const displayCurrency = useSelector(selectDisplayCurrency);
  const totalHeader =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? "total_in_btc"
      : "total_in_usd";
  const availableHeader =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? "available_in_btc"
      : "available_in_usd";

  const getQuantity = (quantity?: number, symbol?: string) => {
    return (
      <TextWithPrecision
        text={quantity}
        precisionType={PrecisionType.QUANTITY}
        precisionSelector={precisionSelector}
        precisionCoin={symbol}
        round={true}
      />
    );
  };

  const getPrice = (coinData: ConsolidatedCoinData) => {
    return (
      <div style={{ minWidth: 100 }}>
        <LiveSymbolPrice
          symbol={getAsset(coinData) || ""}
          exchange={getCoinExchange(coinData) || ""}
          initialPrice={getInitialPrice(coinData) || 0}
          fontSize={
            isBottomNavigator && !isPositions
              ? typography.fontSizes.S
              : typography.fontSizes.M
          }
        />
      </div>
    );
  };

  const getPriceChange = (coinData: ConsolidatedCoinData) => {
    if (!coinData.graph_data) return;
    return (
      <LiveSmallChart
        symbol={getAsset(coinData) || ""}
        exchange={getCoinExchange(coinData) || ""}
        initialGraphData={coinData.graph_data}
        isPrice={true}
        fontSize={isSmallScreen ? 11 : 12}
      />
    );
  };

  const getNewLookPriceTrend = (
    coinData: ConsolidatedCoinData,
    layout?: "inline" | "stacked",
    sparkWidth?: number
  ) => {
    if (!coinData.graph_data) return;
    return (
      <NewLookLiveTrendCell
        symbol={getAsset(coinData) || ""}
        exchange={getCoinExchange(coinData) || ""}
        initialGraphData={coinData.graph_data}
        layout={layout}
        sparkWidth={sparkWidth}
      />
    );
  };

  const getTotalAmount = (coinData: ConsolidatedCoinData) => {
    const precision = getPrecision(
      PrecisionType.AMOUNT,
      coinData.symbol,
      coinData.exchanges[0]
    );
    return (
      <TextWithPrecisionAndIcon
        text={coinData[totalHeader]}
        round={true}
        precision={precision}
      />
    );
  };

  const openCoinExtraDetails = (coinData: ConsolidatedCoinData) => {
    if (isDrawer) return;
    let coinExtraDetailsRoute = getCoinExtraDetailsRoute(
      location.pathname,
      coinData.symbol
    );
    if (isPositions) {
      coinExtraDetailsRoute = getUpdatedPath(
        {
          account_id: coinData.client_ids?.[0]?.toString(),
          margin_type: coinData.margin_type.toString(),
          reverse: `${coinData.is_short}`,
        },
        undefined,
        coinExtraDetailsRoute
      );
    }
    history.push(coinExtraDetailsRoute);
  };

  return {
    displayCurrency,
    availableHeader,
    totalHeader,
    getQuantity,
    getPrice,
    getPriceChange,
    getNewLookPriceTrend,
    getTotalAmount,
    openCoinExtraDetails,
  };
};

export default useAssetItem;
